<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { computed, ref, unref, watch } from 'vue';

import type { ModalDialogApi } from './bootstrap-modal';
import BootstrapModal from './bootstrap-modal.vue';
import type { ConfirmationFunction } from './confirmation-dialog';
import ProgressBar from './progress-bar.vue';
import { formatPercentage, type ProgressApi, ProgressType } from './progress-dialog';

const props = defineProps<{
    modelValue?: boolean;
    percentage?: number;
    progressLabel?: string;
    progressMessage?: string;
    title?: string;
    message?: string;
    messageClass?: string;
    requestCancel?: boolean;
    confirm?: ConfirmationFunction;
}>();
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:modelValue', value: boolean): void;
    (event: 'update:percentage', value: number): void;
    (event: 'update:progressLabel', value: string): void;
    (event: 'update:progressMessage', value: string): void;
    (event: 'update:title', value: string | undefined): void;
    (event: 'update:message', value: string | undefined): void;
    (event: 'update:messageClass', value: string | undefined): void;
    (event: 'update:requestCancel', value: boolean): void;
    (event: 'dialog-closed', value: undefined): void;
}>();
const showDialog = useVModel(props, 'modelValue', emit, { passive: true });
const percentageModel = useVModel(props, 'percentage', emit, { passive: true });
const progressLabelModel = useVModel(props, 'progressLabel', emit, { passive: true });
const progressMessageModel = useVModel(props, 'progressMessage', emit, { passive: true });
const titleModel = useVModel(props, 'title', emit, { passive: true });
const messageModel = useVModel(props, 'message', emit, { passive: true });
const messageClassModel = useVModel(props, 'messageClass', emit, { passive: true });
const requestCancelModel = useVModel(props, 'requestCancel', emit, { passive: true });
const modalApi = ref<ModalDialogApi | undefined>();

const percentage2 = ref<number | undefined>(undefined);
const progressLabel2 = ref<string | undefined>(undefined);
const progressMessage2 = ref<string | undefined>(undefined);

function formatProgressMessage(percentage: number | undefined, progressMessage: string | undefined) {
    if(progressMessage !== undefined) {
        return progressMessage;
    }
    return formatPercentage(percentage);
}

const progressApi: ProgressApi = {
    toggleModal(show) {
        if(show) {
            requestCancelModel.value = false;
        }
        return unref(modalApi)?.toggleModal(show) ?? Promise.resolve();
    },
    setProgress(percentage, progressMessage, type) {
        switch(type) {
            case ProgressType.SECONDARY:
                percentage2.value = percentage;
                progressMessage2.value = formatProgressMessage(percentage, progressMessage);
                break;
            case ProgressType.MAIN:
            default:
                percentageModel.value = percentage;
                progressMessageModel.value = formatProgressMessage(percentage, progressMessage);
                break;
        }
    },
    setProgressLabel(progressLabel, type) {
        switch(type) {
            case ProgressType.SECONDARY:
                progressLabel2.value = progressLabel;
                break;
            case ProgressType.MAIN:
            default:
                progressLabelModel.value = progressLabel;
                break;
        }
    },
    setTitle(_title: string | undefined) {
        titleModel.value = _title;
    },
    setMessage(_message: string | undefined, _messageClass?: string) {
        messageModel.value = _message;
        if(_messageClass !== undefined) {
            messageClassModel.value = _messageClass;
        }
    },
    isCancelled: computed(() => !!unref(requestCancelModel)),
};
defineExpose(progressApi);

function tryCancel() {
    Promise.resolve()
        .then(() => {
            if(props.confirm) {
                return props.confirm('Confermi?', { cancel: true });
            }
            return Promise.resolve();
        })
        .then(() => {
            requestCancelModel.value = true;
        });
}
watch(showDialog, (val, old) => {
    if(val && !old) {
        requestCancelModel.value = false;
    }
}, { immediate: true });
</script>

<template>
    <BootstrapModal
        ref="modalApi"
        v-model:model-value="showDialog"
        :title="titleModel ?? 'Elaborazione...'"
        :ok="requestCancelModel ? 'Attendere...' : 'Annulla'"
        oktype="button"
        :disabled="requestCancelModel"
        static
        @form-submit="tryCancel"
        @dialog-closed="emit('dialog-closed', undefined)"
    >
        <div v-if="percentageModel !== undefined" class="row">
            <div v-if="progressLabelModel !== undefined" class="col-sm-3">
                {{ progressLabelModel }}
            </div>
            <div :class="(progressLabelModel !== undefined) ? 'col-sm-9' : 'col-sm-12'">
                <ProgressBar :percentage="percentageModel">
                    {{ progressMessageModel }}
                </ProgressBar>
            </div>
        </div>

        <div v-if="percentage2 !== undefined" class="row">
            <div v-if="progressLabel2 !== undefined" class="col-sm-3">
                {{ progressLabel2 }}
            </div>
            <div :class="(progressLabel2 !== undefined) ? 'col-sm-9' : 'col-sm-12'">
                <ProgressBar :percentage="percentage2">
                    {{ progressMessage2 }}
                </ProgressBar>
            </div>
        </div>

        <div v-if="messageModel" style="max-height: 20em; width: 100%; overflow: auto; padding-top: 1em;">
            <!-- eslint-disable vue/multiline-html-element-content-newline -->
            <div
                class="alert"
                :class="messageClassModel ?? 'alert-info'"
                role="alert"
                style="white-space: pre-line;"
            >{{ messageModel }}</div>
            <!-- eslint-enable vue/multiline-html-element-content-newline -->
        </div>
    </BootstrapModal>
</template>
